import type { GleanInstallerConfig } from "../api/gleanHandoff";

export type GleanPlatform = "windows" | "macos" | "linux" | "ios" | "android" | "mobile" | "unknown";

type BrowserPlatform = {
  userAgent?: string;
  platform?: string;
  maxTouchPoints?: number;
  userAgentData?: { platform?: string; mobile?: boolean };
};

/** Use only existing low-entropy browser information; mobile must win over desktop hints. */
export function detectGleanPlatform(browser: BrowserPlatform = typeof navigator === "undefined" ? {} : navigator): GleanPlatform {
  const agent = browser.userAgent ?? "";
  const platform = browser.platform ?? "";
  const hinted = browser.userAgentData?.platform?.trim().toLowerCase();
  if (/android/i.test(agent) || hinted === "android") return "android";
  if (/iphone|ipad|ipod/i.test(agent) || hinted === "ios"
    || (/mac/i.test(platform) && (browser.maxTouchPoints ?? 0) > 1)) return "ios";
  if (browser.userAgentData?.mobile || /mobile|windows phone/i.test(agent)) return "mobile";
  if (hinted === "windows") return "windows";
  if (hinted === "macos" || hinted === "mac os") return "macos";
  if (hinted === "linux") return "linux";
  if (hinted || /cros/i.test(agent)) return "unknown";
  if (/windows nt/i.test(agent) || /^win/i.test(platform)) return "windows";
  if (/macintosh|mac os x/i.test(agent) || /^mac/i.test(platform)) return "macos";
  if (/linux/i.test(agent) || /^linux/i.test(platform)) return "linux";
  return "unknown";
}

/** A legacy URL describes Windows only; missing platform releases never borrow it. */
export function gleanInstallerUrl(platform: GleanPlatform, config: GleanInstallerConfig | null): string | null {
  if (platform !== "windows" && platform !== "macos" && platform !== "linux") return null;
  const url = config?.helper_install_urls !== undefined
    ? config.helper_install_urls[platform]
    : platform === "windows" ? config?.helper_install_url ?? null : null;
  if (!url) return null;
  // Catch an obvious platform misconfiguration even when an approved URL is supplied.
  const path = new URL(url, "https://velocity.invalid").pathname;
  if (platform !== "windows" && /(?:\.(?:exe|msi)|\/installer\/windows)\/?$/i.test(path)) return null;
  return url;
}
